import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { useRandomId } from 'js/utils/hooks/useRandomId';

import { Errors } from 'js/design_system/forms/Errors';

import 'app/assets/stylesheets/design_system/form.scss';
import styled from 'astroturf/react';
import _PillButton from "RadioButtonGroup-PillButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/forms/RadioButtonGroup.tsx?styleId=PillButton";


export type RadioButtonGroupProps = {
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** The input's description. Shown directly underneath the label. */
  description?: ReactNode;
  /** The options available */
  options: string[];
  /** The initial value, should be a member of options */
  value?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** The errors to show beneath the field */
  errors?: ReactNode[];
  /** Whether to decorate with error styling */
  hasError?: boolean;
  /** Use the small button variant (default=false) */
  small?: boolean;
  /** Change event for the selection */
  onChange?: (selected: string | undefined) => void;
};

/**
 * # RadioButtonGroup
 * A component wrapping a group of buttons that act as radio buttons.
 * Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `label`: The input's label. Shown at the top of the component.
 * - `description`: The input's description. Shown directly underneath the label.
 * - `options`: The options available.
 * - `value`: The initial value, should be a member of options if given.
 * - `disabled`: Whether the input is disabled
 * - `hasError`: Whether to decorate the input with error styling
 * - `errors`: The errors to show beneath the input
 * - `small`: Use the small button variant
 * - `onChange`: Change event for the input
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { RadioButtonGroup } from 'js/design_system/forms/RadioButtonGroup';
 *
 * <RadioButtonGroup label="Select a choice" onSelect={onSelect} options={
 *   ['Choice A', 'Choice B', 'Choice C']
 * } onChange={(selected: string) => oneof Choice A|Choice B|Choice C|undefined}>
 * ```
 *
 */
export const RadioButtonGroup = ({
  label,
  description,
  options,
  value,
  disabled,
  errors = [],
  hasError = false,
  small = false,
  onChange,
}: RadioButtonGroupProps) => {
  // Random ID for ARIA attributes
  const id = useRandomId();

  const containerClasses = classNames('ds2-5-radio-button-group--container');

  const wrapperClasses = classNames('ds2-5-radio-button-group--wrapper', {
    'ds2-5-radio-button-group--disabled': disabled,
  });

  const labelClasses = label
    ? classNames('ds2-5-form--label', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const descriptionClasses = description
    ? classNames('ds2-5-form--desc', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const [selection, setSelection] = useState<string | undefined>(value);

  const onClick = (selected: string) => {
    return () => {
      if (selected === selection) {
        setSelection(undefined);
        if (onChange) onChange(undefined);
      } else {
        setSelection(selected);
        if (onChange) onChange(selected);
      }
    };
  };

  return (
    <div
      role="group"
      className={containerClasses}
      aria-disabled={disabled}
      aria-labelledby={`${id}-label`}
    >
      <legend>
        {label && (
          <label
            htmlFor={`${id}-input`}
            id={`${id}-label`}
            className={labelClasses}
          >
            {label}
          </label>
        )}
        {description && (
          <aside id={`${id}-desc`} className={descriptionClasses}>
            {description}
          </aside>
        )}
      </legend>
      <div className={wrapperClasses}>
        {options.map((option) => (
          <RadioButton
            key={option}
            disabled={disabled}
            checked={selection === option}
            value={option}
            onClick={onClick(option)}
            small={small}
          />
        ))}
      </div>
      {!disabled && hasError && <Errors id={`${id}-errors`} errors={errors} />}
    </div>
  );
};

type RadioButtonProps = {
  value: string;
  onClick: () => void;
  disabled?: boolean;
  small?: boolean;
  checked?: boolean;
};

const PillButton = /*#__PURE__*/styled("button", null, {
  displayName: "PillButton",
  styles: _PillButton
});

export const RadioButton = (props: RadioButtonProps) => {
  return <PillButton {...props}>{props.value}</PillButton>;
};
